import { useRequest } from '@hourly/hooks/useRequest';
import { useState } from 'react';
import {
  deleteBankAccountValidation,
  getSameDayMicrodepositLink,
  getBankAccountVerification,
  validateBankAccount,
  startSameDayMicrodepositValidation,
} from '../requests/bankAccount.request';

interface UseBankAccountOptions {
  requestSameDayMicrodepositOnMount: boolean;
}

export const useBankAccount = (partnerId: string, options: UseBankAccountOptions) => {
  const [token, setToken] = useState<string>('');
  const [sameDayMicrodepositLink, setSameDayMicrodepositLink] = useState('');

  const { status: getBankAccountTokenStatus, request: getBankAccountToken } = useRequest(
    async () => {
      const token = await getBankAccountVerification(partnerId);
      setToken(token);
    }
  );

  const { request: addBankAccount, status: addBankAccountStatus } = useRequest(
    async (publicToken: string) => {
      await validateBankAccount(partnerId, publicToken);
      cleanToken();
    }
  );

  const { request: getSameDayMicrodeposit, status: getSameDayMicrodepositStatus } = useRequest(
    async () => {
      const link = await getSameDayMicrodepositLink(partnerId);
      setSameDayMicrodepositLink(link);
    },
    { requestOnMount: options.requestSameDayMicrodepositOnMount }
  );

  const { request: startSameDayMicrodeposit, status: startSameDayMicrodepositStatus } = useRequest(
    async (token: string) => {
      startSameDayMicrodepositValidation(partnerId, token).then(getSameDayMicrodeposit);
    }
  );

  const {
    request: deleteBankAccountValidationToken,
    status: deleteBankAccountValidationTokenStatus,
  } = useRequest(async () => {
    await deleteBankAccountValidation(partnerId);
    setSameDayMicrodepositLink('');
    setToken('');
  });

  const cleanToken = () => {
    setToken('');
  };

  const loadSameDayMicrodepositToken = () => {
    setToken(sameDayMicrodepositLink);
  };

  const cleanSameDayMicrodepositLink = () => {
    setSameDayMicrodepositLink('');
  };

  const isBankAccountRequestLoading =
    getBankAccountTokenStatus.isLoading ||
    addBankAccountStatus.isLoading ||
    getSameDayMicrodepositStatus.isLoading ||
    startSameDayMicrodepositStatus.isLoading ||
    deleteBankAccountValidationTokenStatus.isLoading;

  return {
    token,
    isBankAccountRequestLoading,
    isAwaitingMicrodepositVerification: !!sameDayMicrodepositLink,
    getBankAccountToken,
    addBankAccount,
    deleteBankAccountValidationToken,
    loadSameDayMicrodepositToken,
    startSameDayMicrodeposit,
    cleanToken,
    cleanSameDayMicrodepositLink,
  };
};
